import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, Divider, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { AnalyticProfile } from '../../contexts/AnalyticProfileContext';
import type { FrontendNotebook } from '../../contexts/NotebooksContext';
import type { PodcastData } from '../../contexts/PodcastsContext';

function profileNames(ids: string[], profiles: AnalyticProfile[]): string {
  if (!ids.length) return '';
  return ids.map((id) => profiles.find((p) => p.id === id)?.name || id).join(', ');
}

function linkedNotebookSummary(ids: string[], allNotebooks: FrontendNotebook[]): string {
  if (!ids.length) return '';
  return ids.map((id) => allNotebooks.find((n) => n.id === id)?.name || id).join(', ');
}

function notebookAccessLine(nb: FrontendNotebook, profiles: AnalyticProfile[], allNotebooks: FrontendNotebook[]): string {
  const org = 'Always limited to your organization.';
  const base =
    nb.visibility === 'private'
      ? `Private · ${org}`
      : nb.visibility === 'public'
        ? `Organization-wide (any member with access) · ${org}`
        : (() => {
            const names = profileNames(nb.allowed_profiles || [], profiles);
            if (!names) return `Restricted — choose analytic profiles · ${org}`;
            return `Shared with: ${names} · ${org}`;
          })();
  const linked = (nb.linked_notebook_ids || []).filter(Boolean);
  if (!linked.length) return base;
  return `${base} · Linked notebooks: ${linkedNotebookSummary(linked, allNotebooks)}`;
}

function normalizePodcastVisibility(visibility: string | undefined): FrontendNotebook['visibility'] {
  const v = (visibility || 'private').toLowerCase();
  if (v === 'public') return 'public';
  if (v === 'restricted') return 'restricted';
  return 'private';
}

function podcastAccessLine(podcast: PodcastData, profiles: AnalyticProfile[], allNotebooks: FrontendNotebook[]): string {
  const nb: FrontendNotebook = {
    id: podcast.id || '',
    name: podcast.title || '',
    description: '',
    clusters: [],
    cluster_ids: [],
    created_at: '',
    updated_at: '',
    status: 'active',
    visibility: normalizePodcastVisibility(podcast.visibility),
    allowed_profiles: podcast.allowed_profiles || [],
    linked_notebook_ids: podcast.linked_notebook_ids,
    clustersLoaded: false,
  };
  return notebookAccessLine(nb, profiles, allNotebooks);
}

/** Library drawer: control row stays one line; profile + notebook chips share a row below to limit vertical growth. */
const LIBRARY_ACCESS_CONTROL_HEIGHT_PX = 36;

/** Cap profile search width so the notebook link field still grows; wide enough for ~4 words in the input. */
const profileSearchColumnSx = {
  flex: '0 1 auto',
  minWidth: { xs: '100%', sm: 160 },
  maxWidth: { xs: '100%', sm: 312 },
  minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
  display: 'flex',
  alignItems: 'center',
};

type NotebookProps = {
  variant: 'notebook';
  notebook: FrontendNotebook;
  creatorDisplayName: string;
  profiles: AnalyticProfile[];
  /** Active library notebooks (same tenant) for linking. */
  activeNotebooks: FrontendNotebook[];
  /** All loaded notebooks for resolving chip titles (includes non-active). */
  allNotebooks: FrontendNotebook[];
  onSave: (updates: Partial<FrontendNotebook>) => Promise<void> | void;
  readOnly?: boolean;
};

type PodcastProps = {
  variant: 'podcast';
  podcast: PodcastData;
  creatorDisplayName: string;
  profiles: AnalyticProfile[];
  activeNotebooks: FrontendNotebook[];
  allNotebooks: FrontendNotebook[];
  onSave: (updates: Partial<PodcastData>) => Promise<void> | void;
  readOnly?: boolean;
};

export type DraftAccessBarProps = NotebookProps | PodcastProps;

const ACCESS_DEBOUNCE_MS = 450;

const toggleGroupSx = {
  flexShrink: 0,
  '& .MuiToggleButton-root': {
    color: '#d4d4d8',
    borderColor: '#3f3f46',
    textTransform: 'none',
    px: 1,
    py: 0,
    fontSize: '0.75rem',
    lineHeight: 1.2,
    height: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    maxHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    boxSizing: 'border-box',
  },
  '& .MuiToggleButtonGroup-grouped': {
    '&:not(:first-of-type)': { borderLeftColor: '#3f3f46' },
  },
  '& .Mui-selected': {
    color: '#60a5fa !important',
    bgcolor: 'rgba(96,165,250,0.12) !important',
    borderColor: '#60a5fa !important',
  },
};

const profileSearchFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    height: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    maxHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    py: 0,
    fontSize: '0.75rem',
    boxSizing: 'border-box',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    px: 0.75,
    height: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
    right: 6,
  },
};

const notebookLinkFieldSx = profileSearchFieldSx;

function DraftAccessBarNotebook({
  notebook,
  creatorDisplayName,
  profiles,
  activeNotebooks,
  allNotebooks,
  onSave,
  readOnly = false,
}: Omit<NotebookProps, 'variant'>) {
  const [visibility, setVisibility] = useState<FrontendNotebook['visibility']>(notebook.visibility);
  const [allowed, setAllowed] = useState<string[]>(notebook.allowed_profiles || []);
  const [linkedIds, setLinkedIds] = useState<string[]>(notebook.linked_notebook_ids || []);
  const [profilePick, setProfilePick] = useState<AnalyticProfile | null>(null);
  const [notebookPick, setNotebookPick] = useState<FrontendNotebook | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setVisibility(notebook.visibility);
    setAllowed(notebook.allowed_profiles || []);
    setLinkedIds(notebook.linked_notebook_ids || []);
  }, [notebook.id, notebook.visibility, notebook.allowed_profiles, notebook.linked_notebook_ids]);

  const flushSave = useCallback(
    async (vis: FrontendNotebook['visibility'], profIds: string[], linkIds: string[]) => {
      await onSave({
        visibility: vis,
        allowed_profiles: vis === 'restricted' ? profIds : [],
        linked_notebook_ids: linkIds,
      });
    },
    [onSave]
  );

  const queueSave = useCallback(
    (vis: FrontendNotebook['visibility'], profIds: string[], linkIds: string[]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void flushSave(vis, profIds, linkIds);
      }, ACCESS_DEBOUNCE_MS);
    },
    [flushSave]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  if (readOnly) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#71717a' }}>
            Owned by {creatorDisplayName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#86efac', fontSize: '0.75rem' }}>
            Editing locked — ready to publish.
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: '#a1a1aa', display: 'block', mt: 0.35, fontSize: '0.7rem', lineHeight: 1.35 }}
        >
          {notebookAccessLine(notebook, profiles, allNotebooks)}
        </Typography>
      </Box>
    );
  }

  const setVisAndMaybeSave = (vis: FrontendNotebook['visibility']) => {
    setVisibility(vis);
    if (vis === 'private' || vis === 'public') {
      const nextAllowed: string[] = [];
      setAllowed(nextAllowed);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void flushSave(vis, nextAllowed, linkedIds);
      return;
    }
    queueSave(vis, allowed, linkedIds);
  };

  const addProfile = (prof: AnalyticProfile | null) => {
    if (!prof || allowed.includes(prof.id)) return;
    const next = [...allowed, prof.id];
    setAllowed(next);
    setProfilePick(null);
    queueSave('restricted', next, linkedIds);
  };

  const removeProfile = (id: string) => {
    const next = allowed.filter((x) => x !== id);
    setAllowed(next);
    queueSave(visibility === 'restricted' ? 'restricted' : visibility, next, linkedIds);
  };

  const addLinkedNotebook = (nb: FrontendNotebook | null) => {
    if (!nb || nb.id === notebook.id || linkedIds.includes(nb.id)) return;
    const next = [...linkedIds, nb.id];
    setLinkedIds(next);
    setNotebookPick(null);
    queueSave(visibility, allowed, next);
  };

  const removeLinkedNotebook = (id: string) => {
    const next = linkedIds.filter((x) => x !== id);
    setLinkedIds(next);
    queueSave(visibility, allowed, next);
  };

  const profileOptions = profiles.filter((p) => !allowed.includes(p.id));
  const notebookOptions = activeNotebooks.filter((n) => n.id !== notebook.id && !linkedIds.includes(n.id));

  const notebookSearchSlot =
    activeNotebooks.length === 0 ? (
      <Typography variant="caption" sx={{ color: '#71717a', alignSelf: 'center' }}>
        No active notebooks.
      </Typography>
    ) : (
      <Autocomplete
        size="small"
        fullWidth
        options={notebookOptions}
        value={notebookPick}
        onChange={(_, v) => addLinkedNotebook(v)}
        getOptionLabel={(o) => o.name || o.id}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField {...params} placeholder="Link notebooks…" sx={notebookLinkFieldSx} />
        )}
      />
    );

  const profileSearchSlot =
    visibility === 'restricted' ? (
      profiles.length === 0 ? (
        <Typography variant="caption" sx={{ color: '#71717a', alignSelf: 'center' }}>
          No profiles loaded.
        </Typography>
      ) : (
        <Autocomplete
          size="small"
          fullWidth
          options={profileOptions}
          value={profilePick}
          onChange={(_, v) => addProfile(v)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search profiles…" sx={profileSearchFieldSx} />
          )}
        />
      )
    ) : null;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1.25,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#71717a',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            alignSelf: 'flex-start',
            lineHeight: `${LIBRARY_ACCESS_CONTROL_HEIGHT_PX}px`,
          }}
        >
          Owned by {creatorDisplayName}
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: '#3f3f46', alignSelf: 'stretch', my: 0.25, display: { xs: 'none', sm: 'block' } }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={visibility}
          onChange={(_, v) => {
            if (v != null) setVisAndMaybeSave(v);
          }}
          sx={toggleGroupSx}
        >
          <ToggleButton value="private">Private</ToggleButton>
          <ToggleButton value="public">Anyone</ToggleButton>
          <ToggleButton value="restricted">Analytic Profiles</ToggleButton>
        </ToggleButtonGroup>
        {visibility === 'restricted' && (
          <Box sx={profileSearchColumnSx}>
            {profileSearchSlot}
          </Box>
        )}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: '#3f3f46', alignSelf: 'stretch', my: 0.25, display: { xs: 'none', sm: 'block' } }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: 0 },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
          }}
        >
          <Typography variant="caption" sx={{ color: '#a1a1aa', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Link to notebook
          </Typography>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {notebookSearchSlot}
          </Box>
        </Box>
      </Box>
      {((visibility === 'restricted' && allowed.length > 0) || linkedIds.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: { xs: 0.75, sm: 1.5 },
            rowGap: 0.5,
            mt: 0.5,
            width: '100%',
            minWidth: 0,
          }}
        >
          {visibility === 'restricted' && allowed.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: 'flex-start',
                flex: { sm: '1 1 0px' },
                minWidth: { xs: '100%', sm: 0 },
              }}
            >
              {allowed.map((id) => (
                <Chip
                  key={`ap-${id}`}
                  size="small"
                  label={profiles.find((p) => p.id === id)?.name || id}
                  onDelete={() => removeProfile(id)}
                  sx={{ bgcolor: '#27272a', color: '#e4e4e7', flexShrink: 0 }}
                />
              ))}
            </Box>
          )}
          {linkedIds.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                flex: { sm: '1 1 0px' },
                minWidth: { xs: '100%', sm: 0 },
              }}
            >
              {linkedIds.map((id) => (
                <Chip
                  key={`ln-${id}`}
                  size="small"
                  label={allNotebooks.find((n) => n.id === id)?.name || id}
                  onDelete={() => removeLinkedNotebook(id)}
                  sx={{ bgcolor: '#1e3a5f', color: '#bfdbfe', flexShrink: 0 }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function DraftAccessBarPodcast({
  podcast,
  creatorDisplayName,
  profiles,
  activeNotebooks,
  allNotebooks,
  onSave,
  readOnly = false,
}: Omit<PodcastProps, 'variant'>) {
  const [visibility, setVisibility] = useState<FrontendNotebook['visibility']>(() =>
    normalizePodcastVisibility(podcast.visibility)
  );
  const [allowed, setAllowed] = useState<string[]>(podcast.allowed_profiles || []);
  const [linkedIds, setLinkedIds] = useState<string[]>(podcast.linked_notebook_ids || []);
  const [profilePick, setProfilePick] = useState<AnalyticProfile | null>(null);
  const [notebookPick, setNotebookPick] = useState<FrontendNotebook | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setVisibility(normalizePodcastVisibility(podcast.visibility));
    setAllowed(podcast.allowed_profiles || []);
    setLinkedIds(podcast.linked_notebook_ids || []);
  }, [podcast.id, podcast.visibility, podcast.allowed_profiles, podcast.linked_notebook_ids]);

  const flushSave = useCallback(
    async (vis: FrontendNotebook['visibility'], profIds: string[], linkIds: string[]) => {
      await onSave({
        visibility: vis,
        allowed_profiles: vis === 'restricted' ? profIds : [],
        linked_notebook_ids: linkIds,
      });
    },
    [onSave]
  );

  const queueSave = useCallback(
    (vis: FrontendNotebook['visibility'], profIds: string[], linkIds: string[]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void flushSave(vis, profIds, linkIds);
      }, ACCESS_DEBOUNCE_MS);
    },
    [flushSave]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  if (readOnly) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#71717a' }}>
            Owned by {creatorDisplayName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#86efac', fontSize: '0.75rem' }}>
            Editing locked — ready to publish.
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: '#a1a1aa', display: 'block', mt: 0.35, fontSize: '0.7rem', lineHeight: 1.35 }}
        >
          {podcastAccessLine(podcast, profiles, allNotebooks)}
        </Typography>
      </Box>
    );
  }

  const setVisAndMaybeSave = (vis: FrontendNotebook['visibility']) => {
    setVisibility(vis);
    if (vis === 'private' || vis === 'public') {
      const nextAllowed: string[] = [];
      setAllowed(nextAllowed);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void flushSave(vis, nextAllowed, linkedIds);
      return;
    }
    queueSave(vis, allowed, linkedIds);
  };

  const addProfile = (prof: AnalyticProfile | null) => {
    if (!prof || allowed.includes(prof.id)) return;
    const next = [...allowed, prof.id];
    setAllowed(next);
    setProfilePick(null);
    queueSave('restricted', next, linkedIds);
  };

  const removeProfile = (id: string) => {
    const next = allowed.filter((x) => x !== id);
    setAllowed(next);
    queueSave(visibility === 'restricted' ? 'restricted' : visibility, next, linkedIds);
  };

  const addLinkedNotebook = (nb: FrontendNotebook | null) => {
    if (!nb || linkedIds.includes(nb.id)) return;
    const next = [...linkedIds, nb.id];
    setLinkedIds(next);
    setNotebookPick(null);
    queueSave(visibility, allowed, next);
  };

  const removeLinkedNotebook = (id: string) => {
    const next = linkedIds.filter((x) => x !== id);
    setLinkedIds(next);
    queueSave(visibility, allowed, next);
  };

  const profileOptions = profiles.filter((p) => !allowed.includes(p.id));
  const notebookOptions = activeNotebooks.filter((n) => !linkedIds.includes(n.id));

  const notebookSearchSlot =
    activeNotebooks.length === 0 ? (
      <Typography variant="caption" sx={{ color: '#71717a', alignSelf: 'center' }}>
        No active notebooks.
      </Typography>
    ) : (
      <Autocomplete
        size="small"
        fullWidth
        options={notebookOptions}
        value={notebookPick}
        onChange={(_, v) => addLinkedNotebook(v)}
        getOptionLabel={(o) => o.name || o.id}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField {...params} placeholder="Link notebooks…" sx={notebookLinkFieldSx} />
        )}
      />
    );

  const profileSearchSlot =
    visibility === 'restricted' ? (
      profiles.length === 0 ? (
        <Typography variant="caption" sx={{ color: '#71717a', alignSelf: 'center' }}>
          No profiles loaded.
        </Typography>
      ) : (
        <Autocomplete
          size="small"
          fullWidth
          options={profileOptions}
          value={profilePick}
          onChange={(_, v) => addProfile(v)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search profiles…" sx={profileSearchFieldSx} />
          )}
        />
      )
    ) : null;

  return (
    <Box sx={{ width: '100%', minWidth: 0, mb: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1.25,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#71717a',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            alignSelf: 'flex-start',
            lineHeight: `${LIBRARY_ACCESS_CONTROL_HEIGHT_PX}px`,
          }}
        >
          Owned by {creatorDisplayName}
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: '#3f3f46', alignSelf: 'stretch', my: 0.25, display: { xs: 'none', sm: 'block' } }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={visibility}
          onChange={(_, v) => {
            if (v != null) setVisAndMaybeSave(v);
          }}
          sx={toggleGroupSx}
        >
          <ToggleButton value="private">Private</ToggleButton>
          <ToggleButton value="public">Anyone</ToggleButton>
          <ToggleButton value="restricted">Analytic Profiles</ToggleButton>
        </ToggleButtonGroup>
        {visibility === 'restricted' && (
          <Box sx={profileSearchColumnSx}>
            {profileSearchSlot}
          </Box>
        )}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: '#3f3f46', alignSelf: 'stretch', my: 0.25, display: { xs: 'none', sm: 'block' } }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: 0 },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
          }}
        >
          <Typography variant="caption" sx={{ color: '#a1a1aa', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Link to notebook
          </Typography>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: LIBRARY_ACCESS_CONTROL_HEIGHT_PX,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {notebookSearchSlot}
          </Box>
        </Box>
      </Box>
      {((visibility === 'restricted' && allowed.length > 0) || linkedIds.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: { xs: 0.75, sm: 1.5 },
            rowGap: 0.5,
            mt: 0.5,
            width: '100%',
            minWidth: 0,
          }}
        >
          {visibility === 'restricted' && allowed.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: 'flex-start',
                flex: { sm: '1 1 0px' },
                minWidth: { xs: '100%', sm: 0 },
              }}
            >
              {allowed.map((id) => (
                <Chip
                  key={`ap-${id}`}
                  size="small"
                  label={profiles.find((p) => p.id === id)?.name || id}
                  onDelete={() => removeProfile(id)}
                  sx={{ bgcolor: '#27272a', color: '#e4e4e7', flexShrink: 0 }}
                />
              ))}
            </Box>
          )}
          {linkedIds.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                flex: { sm: '1 1 0px' },
                minWidth: { xs: '100%', sm: 0 },
              }}
            >
              {linkedIds.map((id) => (
                <Chip
                  key={`ln-${id}`}
                  size="small"
                  label={allNotebooks.find((n) => n.id === id)?.name || id}
                  onDelete={() => removeLinkedNotebook(id)}
                  sx={{ bgcolor: '#1e3a5f', color: '#bfdbfe', flexShrink: 0 }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export function DraftAccessBar(props: DraftAccessBarProps) {
  if (props.variant === 'notebook') {
    return (
      <DraftAccessBarNotebook
        notebook={props.notebook}
        creatorDisplayName={props.creatorDisplayName}
        profiles={props.profiles}
        activeNotebooks={props.activeNotebooks}
        allNotebooks={props.allNotebooks}
        onSave={props.onSave}
        readOnly={props.readOnly}
      />
    );
  }
  return (
    <DraftAccessBarPodcast
      podcast={props.podcast}
      creatorDisplayName={props.creatorDisplayName}
      profiles={props.profiles}
      activeNotebooks={props.activeNotebooks}
      allNotebooks={props.allNotebooks}
      onSave={props.onSave}
      readOnly={props.readOnly}
    />
  );
}
